import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Sprout, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen w-full items-stretch bg-[#0f2c1e]">
      {/* Panneau gauche illustratif */}
      <div className="relative hidden flex-1 items-center justify-center overflow-hidden lg:flex">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=1400&q=70)",
          }}
        />
        <div className="absolute inset-0 bg-[#0f2c1e]/70" />
        <div className="relative z-10 max-w-md px-12 text-white">
          <div className="mb-6 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#e8c872] text-[#1a4731]">
            <Sprout size={28} />
          </div>
          <h1 className="text-4xl font-bold leading-tight">Champ&nbsp;Cadastre</h1>
          <p className="mt-4 text-lg text-[#cfe0d4]">
            Gérez et visualisez vos parcelles agricoles sur photo aérienne et cadastre.
            Vos données restent privées et accessibles partout.
          </p>
        </div>
      </div>

      {/* Formulaire */}
      <div className="flex w-full items-center justify-center bg-[#f7f6f1] px-6 lg:w-[480px]">
        <form onSubmit={handleSubmit} className="w-full max-w-sm" data-testid="login-form">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-[#1a4731] text-[#e8c872]">
              <Sprout size={22} />
            </span>
            <span className="text-xl font-bold text-[#1a4731]">Champ Cadastre</span>
          </div>

          <h2 className="text-2xl font-bold text-stone-800">Connexion</h2>
          <p className="mt-1 text-sm text-stone-500">Accédez à votre exploitation.</p>

          <div className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email">Adresse email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="vous@exploitation.fr"
                required
                className="mt-1.5"
                data-testid="login-email"
              />
            </div>
            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="mt-1.5"
                data-testid="login-password"
              />
            </div>

            {error && (
              <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="login-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#1a4731] text-white hover:bg-[#143726]"
              data-testid="login-submit"
            >
              {loading && <Loader2 size={16} className="mr-2 animate-spin" />}
              Se connecter
            </Button>
          </div>

          <p className="mt-6 text-center text-xs text-stone-400">
            Inscription réservée — contactez votre administrateur pour obtenir un compte.
          </p>
        </form>
      </div>
    </div>
  );
}
